import { DateTime } from '@ksyos/date-time';
import { Alert } from '@ksyos/design-system';
import { useState } from 'react';
import type { ICustomControlProcedureReadProps } from '../../interfaces/';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { CustomControlStatus } from '../../../../../../common/enums/customControlStatus';
import { FilesUrlBase } from '../../../../../../common/enums/filesUrlBase';
import { ProcedureStatus } from '../../../../../../common/enums/procedureStatus';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { isBrowserInternetExplorer } from '../../../../../../common/utils/isBrowserInternetExplorer';
import { fetchJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { useEpisodeContext } from '../../../../../utils/hooks/useEpisodeContext';
import { IconLink } from '../../../../elements/buttons/iconLink/IconLink';
import { SsoInterapyGradingButton } from '../../../../elements/carePathButtons/SsoInterapyGradingButton';
import { TakeProcedureButton } from '../../../../elements/carePathButtons/TakeProcedureButton';
import { TakeProcedureFromOrganizationButton } from '../../../../elements/carePathButtons/TakeProcedureFromOrganizationButton';
import { ButtonContainer, DangerAlertWhenErrorMessage, FormParagraph } from '../../../../elements/common';
import { FormGroup } from '../../../../elements/forms/FormGroup';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { List } from '../../../../elements/List/List';
import { ViewerModal } from '../../../../elements/viewerModal/ViewerModal';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { GgzoIntV1ProcedureSummary } from './GgzoIntV1ProcedureSummary';

export function GgzoIntV1ProcedureRead(props: ICustomControlProcedureReadProps) {
    const { patient } = useEpisodeContext();
    const [ssoErrorMessage, setSsoErrorMessage] = useState<string | undefined>();
    const [showAnswersViewerModal, setShowAnswersViewerModal] = useState(false);

    return (
        <ProcessContainer isActiveActivity={props.itemState.initialIsOpen}>
            <ProcessItemHeader
                date={props.process.procedure && props.process.procedure.dateSent}
                isFirstItem={props.isFirstItem}
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) =>
                    await props.updateItemState({
                        ...props.itemState,
                        isOpen: isOpened || !!props.itemState.initialIsOpen,
                    })
                }
                practitionerName={getDisplayName(patient)}
                process={props.process}
                serviceType={props.process.serviceType}
                showProcessIcon
                title={
                    getServiceLabel(props.process.code, 'procedure', props.process.name) +
                    (props.procedure.customControlData && props.procedure.customControlData.isNameting
                        ? ' Nameting'
                        : '')
                }
            />

            {props.procedure.status === ProcedureStatus.active &&
                props.procedure.customControlStatus === CustomControlStatus.withPatient && (
                    <Alert appearance="info">{getDisplayName(patient)} heeft het onderzoek nog niet ingevuld</Alert>
                )}

            {props.procedure.status === ProcedureStatus.completed && props.process.diagnosticReports.length === 0 && (
                <>
                    <Alert appearance="success">
                        {getDisplayName(patient)} heeft de vragenlijst ingevuld op{' '}
                        {props.procedure.customControlData.submitDatetime
                            ? `${DateTime.from(props.procedure.customControlData.submitDatetime).format('DD-MM-YYYY')}`
                            : '-'}
                    </Alert>
                    <DangerAlertWhenErrorMessage errorMessage={ssoErrorMessage} />
                    <ButtonContainer
                        alignRight
                        buttons={[
                            <TakeProcedureButton
                                procedure={props.procedure}
                                reloadProcess={props.reloadProcess}
                                request={props.request}
                            />,
                            <TakeProcedureFromOrganizationButton
                                procedure={props.procedure}
                                reloadProcess={props.reloadProcess}
                                request={props.request}
                            />,
                            <SsoInterapyGradingButton
                                request={props.request}
                                ssoToInterapy={ssoToInterapy}
                            />,
                        ]}
                    />
                </>
            )}

            {props.procedure.status === ProcedureStatus.completed && props.process.diagnosticReports.length === 1 && (
                <>
                    <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                        <GgzoIntV1ProcedureSummary {...props} />

                        {props.procedure.customControlData.beloopKlachten &&
                            props.procedure.customControlData.beloopKlachten.length > 0 && (
                                <HorizontalFormGroupRead>
                                    <FormLabel>{translateKey('interapy.courseOfComplaints')}</FormLabel>
                                    <InputContainer>
                                        <List
                                            bold
                                            items={props.procedure.customControlData.beloopKlachten}
                                            readVariant="stacked"
                                        />
                                    </InputContainer>
                                </HorizontalFormGroupRead>
                            )}

                        {props.procedure.customControlData.duurKlachten &&
                            props.procedure.customControlData.duurKlachten.length > 0 && (
                                <HorizontalFormGroupRead>
                                    <FormLabel>{translateKey('interapy.durationOfComplaints')}</FormLabel>
                                    <InputContainer>
                                        <List
                                            bold
                                            items={props.procedure.customControlData.duurKlachten}
                                            readVariant="stacked"
                                        />
                                    </InputContainer>
                                </HorizontalFormGroupRead>
                            )}

                        {props.procedure.customControlData.klachtenBeschrijvingClient && (
                            <HorizontalFormGroupRead>
                                <FormLabel>{translateKey('interapy.clientComplaintsDescription')}</FormLabel>
                                <InputContainer>
                                    <FormParagraph>
                                        {props.procedure.customControlData.klachtenBeschrijvingClient}
                                    </FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        {props.procedure.customControlData.klachtenDoel && (
                            <HorizontalFormGroupRead>
                                <FormLabel>{translateKey('interapy.purposeOfTreatment')}</FormLabel>
                                <InputContainer>
                                    <FormParagraph>{props.procedure.customControlData.klachtenDoel}</FormParagraph>
                                </InputContainer>
                            </HorizontalFormGroupRead>
                        )}

                        <FormGroup>
                            <IconLink
                                icon="download"
                                iconPostion="left"
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleDownload();
                                }}
                                text="Bekijk alle antwoorden in PDF"
                                to="#"
                            />
                        </FormGroup>

                        {showAnswersViewerModal && (
                            <ViewerModal
                                assets={[]}
                                id={props.procedure.id}
                                isPdf
                                onClose={() => setShowAnswersViewerModal(false)}
                                selectedFileId={props.procedure.customControlData.antwoordenPdfAssetId}
                                url=""
                                urlBase={FilesUrlBase.procedures}
                            />
                        )}
                    </CustomControlCollapseBody>

                    {!props.itemState.initialIsOpen && (
                        <ProcessItemFooter
                            isItemOpen={props.itemState.isOpen}
                            labelLess={translateKey('procedure.hideProcedure')}
                            labelMore={translateKey('procedure.showProcedure')}
                            onToggle={async (isOpened) =>
                                await props.updateItemState({ ...props.itemState, isOpen: isOpened })
                            }
                        />
                    )}
                </>
            )}
        </ProcessContainer>
    );

    async function ssoToInterapy() {
        try {
            const result = await fetchJson(`/api/procedures/${props.procedure.id}/gradingSsoLink`, 'POST');
            window.location.href = result.json.gradingSsoLink;
        } catch {
            setSsoErrorMessage('Er ging iets fout, probeer het opnieuw!');
        }
    }

    async function handleDownload() {
        if (isBrowserInternetExplorer()) {
            window.open(
                `/api/${FilesUrlBase.procedures}/${props.procedure.id}/assets/${props.procedure.customControlData.antwoordenPdfAssetId}`
            );
        } else {
            setShowAnswersViewerModal(true);
        }
    }
}
